import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { z } from "zod";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { CardEditor } from "@/components/CardEditor";
import {
  Edit,
  Trash2,
  PlusCircle,
  RefreshCw,
  Check,
  X,
  Images,
  Coins,
  Gem,
  Grid,
} from "lucide-react";

const Membership = {
  Free: "free",
  Premium: "premium",
  Exclusive: "exclusive",
} as const;

const DeckType = {
  Common: "common",
  Uncommon: "uncommon",
  Rare: "rare",
  Mythical: "mythical",
  Legendary: "legendary",
  Arcana: "arcana",
} as const;

const CurrencyType = {
  Regular: "regular",
  Premium: "premium",
} as const;

type Membership = typeof Membership[keyof typeof Membership];
type DeckType = typeof DeckType[keyof typeof DeckType];
type CurrencyType = typeof CurrencyType[keyof typeof CurrencyType];

type FormData = {
  name: string;
  deckUrl: string;
  membership: Membership;
  deckType: DeckType;
  isTradeable: boolean;
  isActive: boolean;
  managerMode: boolean;
  price: number;
  currencyType: CurrencyType;
  expiryDate: string;
};

const deckFormSchema = z.object({
  name: z
    .string()
    .min(1, "Deck name is required")
    .max(50, "Deck name too long"),
  deckUrl: z.string().optional(),
  membership: z.enum([Membership.Free, Membership.Premium, Membership.Exclusive]),
  deckType: z.enum([DeckType.Common, DeckType.Uncommon, DeckType.Rare, DeckType.Mythical, DeckType.Legendary, DeckType.Arcana]),
  isTradeable: z.boolean(),
  isActive: z.boolean(),
  managerMode: z.boolean(),
  price: z.number().nonnegative(),
  currencyType: z.enum([CurrencyType.Regular, CurrencyType.Premium]),
  expiryDate: z.string().optional(),
});

type Deck = z.infer<typeof deckFormSchema> & {
  id: number;
};

// Helper function to sanitize deck names for folder creation
const sanitizeFolderName = (name: string): string => {
  return name.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '').toLowerCase();
};

export function CardDecksTab() {
  const { toast } = useToast();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedDeck, setSelectedDeck] = useState<Deck | null>(null);
  const [isCardEditorOpen, setIsCardEditorOpen] = useState(false);
  const [cardDeckId, setCardDeckId] = useState<number | null>(null);
  const [createdDeckId, setCreatedDeckId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    deckUrl: "",
    membership: Membership.Free,
    deckType: DeckType.Common,
    isTradeable: false,
    isActive: false,
    managerMode: false,
    price: 0,
    currencyType: CurrencyType.Regular,
    expiryDate: "",
  });

  // Initialize folder mutation
  const initializeFolderMutation = useMutation({
    mutationFn: async (deckId: number) => {
      // Find the deck to get its name
      const deckToInitialize = decks?.find(d => d.id === deckId);
      const sanitizedFolderName = deckToInitialize ? sanitizeFolderName(deckToInitialize.name) : '';
      
      return apiRequest<any>(`/api/manager/decks/${deckId}/initialize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ folderName: sanitizedFolderName }),
      });
    },
    onSuccess: (data) => {
      toast({
        title: "Folder initialized",
        description: "Deck folder has been created on storage.",
      });
      
      // Update the deckUrl field with the returned URL
      if (data && data.deckUrl) {
        setFormData(prev => ({
          ...prev,
          deckUrl: data.deckUrl
        }));
      }
      
      // Refresh the deck data
      queryClient.invalidateQueries({ queryKey: ["/api/manager/decks"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Initialization failed",
        description: error.message || "Failed to initialize deck folder.",
        variant: "destructive",
      });
    },
  });

  // Query to get all decks
  const {
    data: decks,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["/api/manager/decks"],
    queryFn: () => apiRequest<any[]>("/api/manager/decks"),
  });

  // Mutation to add a new deck
  const addDeckMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest<any>("/api/manager/decks", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/manager/decks"] });
      
      // If we have deck data with an ID
      if (data && data.id) {
        // Close the add dialog
        setIsAddDialogOpen(false);
        
        // Set the newly created deck as the selected deck for editing
        setSelectedDeck({
          ...data,
          membership: data.membership || Membership.Free,
          deckType: data.deckType || DeckType.Common,
          currencyType: data.currencyType || CurrencyType.Regular
        });
        
        // Open the edit dialog
        setIsEditDialogOpen(true);
        
        toast({
          title: "Deck added successfully",
          description: "Now you can initialize the folder for this deck.",
        });
      } else {
        toast({
          title: "Deck added successfully",
          description: "The new card deck has been added to the system.",
        });
        
        setIsAddDialogOpen(false);
        resetForm();
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding deck",
        description:
          error.message || "Failed to add the deck. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to update a deck
  const updateDeckMutation = useMutation({
    mutationFn: async (data: { id: number; deck: any }) => {
      return apiRequest<any>(`/api/manager/decks/${data.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data.deck),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/manager/decks"] });
      toast({
        title: "Deck updated successfully",
        description: "The card deck has been updated.",
      });
      setIsEditDialogOpen(false);
      setSelectedDeck(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating deck",
        description:
          error.message || "Failed to update the deck. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Mutation to delete a deck
  const deleteDeckMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest<any>(`/api/manager/decks/${id}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/manager/decks"] });
      toast({
        title: "Deck deleted successfully",
        description: "The card deck has been removed from the system.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting deck",
        description:
          error.message || "Failed to delete the deck. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validatedData = deckFormSchema.parse(formData);
      addDeckMutation.mutate(validatedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors.map((e) => e.message).join(", ");
        toast({
          title: "Validation Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDeck) return;

    try {
      const validatedData = deckFormSchema.parse(formData);
      updateDeckMutation.mutate({ id: selectedDeck.id, deck: validatedData });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors.map((e) => e.message).join(", ");
        toast({
          title: "Validation Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    }
  };

  const handleDelete = (id: number) => {
    deleteDeckMutation.mutate(id);
  };

  const openEditDialog = (deck: Deck) => {
    setSelectedDeck(deck);
    setFormData({
      name: deck.name,
      deckUrl: deck.deckUrl ?? "",
      membership: deck.membership as Membership,
      deckType: deck.deckType as DeckType,
      isTradeable: deck.isTradeable,
      isActive: deck.isActive,
      managerMode: deck.managerMode,
      price: Number(deck.price),
      currencyType: (deck.currencyType ?? CurrencyType.Regular) as CurrencyType,
      expiryDate: deck.expiryDate ?? "",
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      deckUrl: "",
      membership: Membership.Free,
      deckType: DeckType.Standard,
      isTradeable: false,
      isActive: false,
      managerMode: false,
      price: 0,
      currencyType: CurrencyType.Regular,
      expiryDate: "",
    });
  };

  const getMembershipClass = (membership: string) => {
    switch (membership) {
      case "premium":
        return "bg-yellow-100 text-yellow-800";
      case "exclusive":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-green-100 text-green-800";
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading decks</h3>
        <p className="text-muted-foreground">{error.message}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() =>
            queryClient.invalidateQueries({ queryKey: ["/api/manager/decks"] })
          }
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => {
                resetForm();
                setIsAddDialogOpen(true);
              }}
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add New Deck
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>Add New Card Deck</DialogTitle>
              <DialogDescription>
                Enter the details for the new card deck.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddSubmit}>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Deck Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="Enter deck name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deckUrl">Deck URL (Add deck then initialize folder)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="deckUrl"
                        value={formData.deckUrl}
                        onChange={(e) =>
                          setFormData({ ...formData, deckUrl: e.target.value })
                        }
                        placeholder="Will be set when initializing folder"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (createdDeckId) {
                            initializeFolderMutation.mutate(createdDeckId);
                          } else {
                            toast({
                              title: "Save deck first",
                              description: "You need to save the deck before initializing its folder.",
                              variant: "default",
                            });
                          }
                        }}
                        disabled={initializeFolderMutation.isPending || !createdDeckId}
                        className="whitespace-nowrap"
                      >
                        {initializeFolderMutation.isPending ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Grid className="h-4 w-4 mr-2" />
                        )}
                        Initialize Folder
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="membership">Membership</Label>
                    <Select
                      value={formData.membership}
                      onValueChange={(value: Membership) =>
                        setFormData({ ...formData, membership: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select membership" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={Membership.Free}>Free</SelectItem>
                        <SelectItem value={Membership.Premium}>Premium</SelectItem>
                        <SelectItem value={Membership.Exclusive}>Exclusive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deckType">Deck Type</Label>
                    <Select
                      value={formData.deckType}
                      onValueChange={(value: DeckType) =>
                        setFormData({ ...formData, deckType: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select deck type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DeckType.Common}>Common</SelectItem>
                        <SelectItem value={DeckType.Uncommon}>Uncommon</SelectItem>
                        <SelectItem value={DeckType.Rare}>Rare</SelectItem>
                        <SelectItem value={DeckType.Mythical}>Mythical</SelectItem>
                        <SelectItem value={DeckType.Legendary}>Legendary</SelectItem>
                        <SelectItem value={DeckType.Arcana}>Arcana</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price">Price ({formData.currencyType === CurrencyType.Premium ? "Gems" : "Coins"})</Label>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      step="1"
                      pattern="\d*"
                      value={Math.floor(formData.price)}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          price: Math.floor(parseInt(e.target.value) || 0),
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currencyType">Currency Type</Label>
                    <Select
                      value={formData.currencyType}
                      onValueChange={(value: CurrencyType) =>
                        setFormData({ ...formData, currencyType: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select currency type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={CurrencyType.Regular}>
                          <span className="flex items-center">
                            <Coins className="h-4 w-4 mr-2 text-yellow-500" />
                            Regular (Coins)
                          </span>
                        </SelectItem>
                        <SelectItem value={CurrencyType.Premium}>
                          <span className="flex items-center">
                            <Gem className="h-4 w-4 mr-2 text-purple-500" />
                            Premium (Gems)
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expiryDate">Expiry Date (Optional)</Label>
                    <Input
                      id="expiryDate"
                      type="date"
                      value={formData.expiryDate}
                      onChange={(e) =>
                        setFormData({ ...formData, expiryDate: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isTradeable"
                      checked={formData.isTradeable}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, isTradeable: checked })
                      }
                    />
                    <Label htmlFor="isTradeable">Tradeable</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="isActive"
                      checked={formData.isActive}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, isActive: checked })
                      }
                    />
                    <Label htmlFor="isActive">Active</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="managerMode"
                      checked={formData.managerMode}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, managerMode: checked })
                      }
                    />
                    <Label htmlFor="managerMode">Manager Mode</Label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={addDeckMutation.isPending}
                  className="ml-2"
                >
                  {addDeckMutation.isPending ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>Add Deck</>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Table>
        <TableCaption>A list of all card decks in the system.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Membership</TableHead>
            <TableHead>Price</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Manager Mode</TableHead>
            <TableHead>Tradeable</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {!decks || decks.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center">
                No card decks found. Add your first deck to get started.
              </TableCell>
            </TableRow>
          ) : (
            decks.map((deck) => (
              <TableRow key={deck.id}>
                <TableCell className="font-medium">{deck.id}</TableCell>
                <TableCell>{deck.name}</TableCell>
                <TableCell>
                  <span className="capitalize">{deck.deckType}</span>
                </TableCell>
                <TableCell>
                  <span
                    className={`px-2 py-1 rounded-md text-xs font-medium ${getMembershipClass(deck.membership)}`}
                  >
                    {deck.membership}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    {deck.currencyType === "premium" ? (
                      <Gem className="h-4 w-4 mr-1 text-purple-500" />
                    ) : (
                      <Coins className="h-4 w-4 mr-1 text-yellow-500" />
                    )}
                    {parseFloat(deck.price) > 0 ? (
                      <span>{Math.floor(parseFloat(deck.price))} {deck.currencyType === CurrencyType.Premium ? "Gems" : "Coins"}</span>
                    ) : (
                      "Free"
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {deck.isActive ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-100 text-green-800">
                      <Check className="h-3 w-3 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                      <X className="h-3 w-3 mr-1" />
                      Inactive
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {deck.managerMode ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                      <Check className="h-3 w-3 mr-1" />
                      Enabled
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                      <X className="h-3 w-3 mr-1" />
                      Disabled
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  {deck.isTradeable ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-100 text-emerald-800">
                      <Check className="h-3 w-3 mr-1" />
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800">
                      <X className="h-3 w-3 mr-1" />
                      No
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => {
                        setCardDeckId(deck.id);
                        setIsCardEditorOpen(true);
                      }}
                    >
                      <Images className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => openEditDialog(deck)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Card Deck</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{deck.name}"? This
                            action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(deck.id)}
                            className="bg-destructive text-destructive-foreground"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Card Deck</DialogTitle>
            <DialogDescription>
              Update the details for this card deck.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Deck Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Enter deck name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-deckUrl">Deck URL</Label>
                  <div className="flex gap-2">
                    <Input
                      id="edit-deckUrl"
                      value={formData.deckUrl}
                      onChange={(e) =>
                        setFormData({ ...formData, deckUrl: e.target.value })
                      }
                      placeholder="Enter deck URL"
                      className="flex-1"
                    />
                    {selectedDeck && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (selectedDeck && selectedDeck.id) {
                            initializeFolderMutation.mutate(selectedDeck.id);
                          }
                        }}
                        disabled={initializeFolderMutation.isPending}
                        className="whitespace-nowrap"
                      >
                        {initializeFolderMutation.isPending ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Grid className="h-4 w-4 mr-2" />
                        )}
                        Initialize Folder
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-membership">Membership</Label>
                  <Select
                    value={formData.membership}
                    onValueChange={(value: Membership) =>
                      setFormData({ ...formData, membership: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select membership" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={Membership.Free}>Free</SelectItem>
                      <SelectItem value={Membership.Premium}>Premium</SelectItem>
                      <SelectItem value={Membership.Exclusive}>Exclusive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-deckType">Deck Type</Label>
                  <Select
                    value={formData.deckType}
                    onValueChange={(value: DeckType) =>
                      setFormData({ ...formData, deckType: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select deck type" />
                    </SelectTrigger>
                    <SelectContent>
                    <SelectItem value={DeckType.Common}>Common</SelectItem>
                        <SelectItem value={DeckType.Uncommon}>Uncommon</SelectItem>
                        <SelectItem value={DeckType.Rare}>Rare</SelectItem>
                        <SelectItem value={DeckType.Mythical}>Mythical</SelectItem>
                        <SelectItem value={DeckType.Legendary}>Legendary</SelectItem>
                        <SelectItem value={DeckType.Arcana}>Arcana</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-price">Price ({formData.currencyType === CurrencyType.Premium ? "Gems" : "Coins"})</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    min="0"
                    step="1"
                    pattern="\d*"
                    value={Math.floor(formData.price)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        price: Math.floor(parseInt(e.target.value) || 0),
                      })
                    }
                    placeholder="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-currencyType">Currency Type</Label>
                  <Select
                    value={formData.currencyType}
                    onValueChange={(value: CurrencyType) =>
                      setFormData({ ...formData, currencyType: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CurrencyType.Regular}>
                        <span className="flex items-center">
                          <Coins className="h-4 w-4 mr-2 text-yellow-500" />
                          Regular (Coins)
                        </span>
                      </SelectItem>
                      <SelectItem value={CurrencyType.Premium}>
                        <span className="flex items-center">
                          <Gem className="h-4 w-4 mr-2 text-purple-500" />
                          Premium (Gems)
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-expiryDate">Expiry Date (Optional)</Label>
                  <Input
                    id="edit-expiryDate"
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      setFormData({ ...formData, expiryDate: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="edit-isTradeable"
                    checked={formData.isTradeable}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, isTradeable: checked })
                    }
                  />
                  <Label htmlFor="edit-isTradeable">Tradeable</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="edit-isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, isActive: checked })
                    }
                  />
                  <Label htmlFor="edit-isActive">Active</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="edit-managerMode"
                    checked={formData.managerMode}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, managerMode: checked })
                    }
                  />
                  <Label htmlFor="edit-managerMode">Manager Mode</Label>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updateDeckMutation.isPending}
                className="ml-2"
              >
                {updateDeckMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>Save Changes</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Card Editor Dialog */}
      <Dialog open={isCardEditorOpen} onOpenChange={setIsCardEditorOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Card Editor</DialogTitle>
            <DialogDescription>
              Manage individual card files for the selected deck.
            </DialogDescription>
          </DialogHeader>
          {cardDeckId && <CardEditor deckId={cardDeckId} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}